/**
 * Pre-configured application-level FetchHttpClient instance.
 *
 * DIP: Consumers import this instance (or the IHttpClient type) so they
 * are never coupled to the FetchHttpClient constructor.
 *
 * OCP: Swap the export for an AxiosHttpClient at any time without modifying
 * feature code.
 */

import { FetchHttpClient } from './fetch-http-client';
import type { IHttpClient } from './http-client.interface';
import type { HttpRequest } from './types';
import { HttpResponseError } from './http-error';
import { LocalStorage } from '../../store/local-storage';
import { routes } from '../../constants/routing';
import { AuthService } from '../../APIService/AuthService';
import { userLoginData } from '../../localisation/ApiUrls.en';

// Warn early when required environment variables are absent.
if (!process.env.REACT_APP_API_BASE_URL) {
    console.warn('[HttpClient] REACT_APP_API_BASE_URL is not set. API calls will use a relative base URL.');
}
if (!process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY) {
    console.warn('[HttpClient] REACT_APP_OCP_APIM_SUBSCRIPTION_KEY is not set. API Management subscription checks may fail.');
}

/**
 * Request interceptor: attaches the Ocp-Apim-Subscription-Key header.
 *
 * The value is read from the environment variable at request time rather than
 * being frozen into defaultHeaders at singleton construction time. This ensures
 * the header is always sent with the current value and is never silently omitted
 * if the variable was not yet resolved when the module was first loaded.
 *
 * The header is only added when the variable is non-empty so that API Management
 * returns a clear "missing subscription key" error rather than an opaque
 * rejection of an empty-value header.
 */
const subscriptionKeyInterceptor = (request: HttpRequest): HttpRequest => {
    const key = process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY;
    if (!key) return request;
    return {
        ...request,
        headers: {
            ...request.headers,
            'Ocp-Apim-Subscription-Key': key,
        },
    };
};

/**
 * Request interceptor: attaches the Bearer token from LocalStorage.
 * Runs before every outgoing request.
 */
const authInterceptor = (request: HttpRequest): HttpRequest => {
    try {
        const userData = LocalStorage.getUserData();
        const token: string | undefined = userData?.token ?? userData?.access_token;
        if (token) {
            return {
                ...request,
                headers: {
                    ...request.headers,
                    Authorization: `Bearer ${token}`,
                },
            };
        }
    } catch {
        // Silently continue – unauthenticated requests will fail at the server
    }
    return request;
};

/**
 * Error interceptor: attempt a silent token refresh on 401, then fall back to logout.
 *
 * Flow:
 *  1. On 401, try to obtain a new access token via the refresh-token grant.
 *  2. If the refresh succeeds, persist the new tokens and return a non-HttpResponseError
 *     so the retry loop in FetchHttpClient re-runs the request with the fresh token.
 *  3. If the refresh fails (expired refresh token, network error, etc.), clear
 *     credentials and redirect to the login route.
 *
 * Concurrency: a single in-flight refresh promise is shared across all concurrent
 * 401 responses so only one refresh call is made at a time.
 *
 * Recursion guard: `isRefreshingToken` prevents the refresh call itself (which
 * goes through httpClient) from triggering another refresh attempt if the
 * refresh endpoint also returns 401.
 */

let isRefreshingToken = false;
let refreshPromise: Promise<boolean> | null = null;

async function performTokenRefresh(): Promise<boolean> {
    isRefreshingToken = true;
    try {
        const userData = await userLoginData();
        const refreshToken: string | undefined = userData?.refresh_token;
        if (!refreshToken) return false;

        const response: any = await AuthService.refreshToken({ refresh_token: refreshToken });
        if (response.success && response.data) {
            // Persist new tokens so the auth request-interceptor picks them up.
            LocalStorage.userData(response.data);
            return true;
        }
        return false;
    } catch {
        return false;
    } finally {
        isRefreshingToken = false;
    }
}

/**
 * Ensures only one refresh is in-flight at a time.
 * Concurrent callers share the same promise.
 */
function refreshTokenOnce(): Promise<boolean> {
    refreshPromise ??= performTokenRefresh().finally(() => {
        refreshPromise = null;
    });
    return refreshPromise;
}

function forceLogout(error: Error): Error {
    try {
        LocalStorage.clearAuthCredentials();
        localStorage.removeItem('UserProfile');
    } catch {
        // ignore storage errors
    }

    const loginRoute: string = routes.LOGIN ?? '/custom-login-route';
    const currentPath = window.location.pathname || '';
    if (!currentPath.includes(loginRoute)) {
        try {
            window.history.replaceState({}, '', loginRoute);
        } catch {
            // ignore navigation errors
        }
    }

    return error;
}

const authErrorInterceptor = async (error: Error): Promise<Error> => {
    if (!(error instanceof HttpResponseError) || error.status !== 401) {
        return error;
    }

    // Guard: if the refresh call itself returned 401, pass the error through
    // without recursing. The outer performTokenRefresh will catch and return false.
    if (isRefreshingToken) {
        return error;
    }

    // Don't attempt refresh on the login route — auth errors are expected there.
    const loginRoute: string = routes.LOGIN ?? '/custom-login-route';
    const currentPath = window.location.pathname || '';
    if (currentPath.includes(loginRoute)) {
        return error;
    }

    // Attempt a silent token refresh.
    const refreshed = await refreshTokenOnce();

    if (refreshed) {
        // Return a plain Error (not HttpResponseError) so the retry loop in
        // FetchHttpClient treats this as a transient/retryable failure and
        // re-runs request interceptors to pick up the fresh token.
        return new Error('Token refreshed, retrying request');
    }

    // Refresh failed — force logout.
    return forceLogout(error);
};

export const httpClient: IHttpClient = new FetchHttpClient({
    baseUrl: process.env.REACT_APP_API_BASE_URL ?? '',
    defaultTimeoutMs: 30_000,
    maxRetries: 3,
    retryBaseDelayMs: 300,
    defaultHeaders: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
    // subscriptionKeyInterceptor runs first so its header is available to
    // subsequent interceptors (e.g. authInterceptor) if they need to inspect it.
    requestInterceptors: [subscriptionKeyInterceptor, authInterceptor],
    errorInterceptors: [authErrorInterceptor],
});

